import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActivityById } from '../../store/activitySlice';
import { RootState, AppDispatch } from '../../store';
import {
  ArrowLeftIcon,
  ClockIcon,
  UserIcon,
  DocumentTextIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

const ActivityDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { currentActivity, loading, error } = useSelector(
    (state: RootState) => state.activities
  );

  useEffect(() => {
    if (id) {
      dispatch(fetchActivityById(id));
    }
  }, [dispatch, id]);

  const getActivityIcon = (type: string) => {
    const icons: Record<string, string> = {
      PO_CREATED: '📋',
      PO_ACCEPTED: '✅',
      PO_REJECTED: '❌',
      PO_UPDATED: '✏️',
      PO_CANCELLED: '🚫',
      USER_CREATED: '👤',
      INVOICE_CREATED: '🧾',
      DISPATCH_CREATED: '📦',
      GRN_CREATED: '📥',
      QC_CREATED: '🔍',
    };
    return icons[type] || '📌';
  };

  const getActivityColor = (type: string) => {
    const colors: Record<string, string> = {
      PO_CREATED: 'bg-blue-100 text-blue-700 border-blue-200',
      PO_ACCEPTED: 'bg-green-100 text-green-700 border-green-200',
      PO_REJECTED: 'bg-red-100 text-red-700 border-red-200',
      PO_UPDATED: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      PO_CANCELLED: 'bg-gray-100 text-gray-700 border-gray-200',
      USER_CREATED: 'bg-purple-100 text-purple-700 border-purple-200',
      INVOICE_CREATED: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      DISPATCH_CREATED: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      GRN_CREATED: 'bg-teal-100 text-teal-700 border-teal-200',
      QC_CREATED: 'bg-orange-100 text-orange-700 border-orange-200',
    };
    return colors[type] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-700">{error}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 text-red-600 hover:text-red-800 underline"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!currentActivity) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <p className="text-yellow-700">Activity not found</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 text-yellow-600 hover:text-yellow-800 underline"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Dashboard
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Activity Details</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Activity Type Badge */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <div className="text-4xl">{getActivityIcon(currentActivity.type)}</div>
              <div className="flex-1">
                <div
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getActivityColor(
                    currentActivity.type
                  )}`}
                >
                  {currentActivity.type.replace(/_/g, ' ')}
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mt-2">
                  {currentActivity.description}
                </h2>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <InformationCircleIcon className="h-5 w-5 mr-2 text-gray-500" />
              Details
            </h3>
            <p className="text-gray-700">{currentActivity.description}</p>
          </div>

          {/* Metadata */}
          {currentActivity.metadata && Object.keys(currentActivity.metadata).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-gray-500" />
                Additional Information
              </h3>
              <div className="space-y-3">
                {Object.entries(currentActivity.metadata).map(([key, value]) => (
                  <div key={key} className="border-b border-gray-100 pb-3 last:border-b-0">
                    <p className="text-sm font-medium text-gray-500 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Entity Information */}
          {currentActivity.entityType && currentActivity.entityId && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Entity</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Entity Type</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {currentActivity.entityType}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Entity ID</p>
                  <p className="mt-1 text-sm font-mono text-gray-900 text-xs">
                    {currentActivity.entityId}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Timestamp */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-gray-500" />
              Timestamp
            </h3>
            <p className="text-sm text-gray-700">
              {formatDateTime(currentActivity.timestamp || currentActivity.createdAt)}
            </p>
          </div>

          {/* User Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
              Performed By
            </h3>
            {currentActivity.userId ? (
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {currentActivity.userId.firstName} {currentActivity.userId.lastName}
                </p>
                <p className="text-xs text-gray-500 mt-1">{currentActivity.userId.email}</p>
                {currentActivity.userId.role && (
                  <span className="inline-block mt-2 px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">
                    {currentActivity.userId.role}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">User information not available</p>
            )}
          </div>

          {/* Activity ID */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity ID</h3>
            <p className="text-xs font-mono text-gray-600 break-all">
              {currentActivity._id || currentActivity.id || id}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityDetails;
